import { CSS_COLUMN_RESIZE_HANDLE, CSS_ROW_RESIZE_HANDLE, CSS_SELECTION_LINE, CSS_TABLE_TOOLBAR, DATA_WEEK_OVERVIEW, QUILL_BETTER_TABLE_REFLOW_DELAY } from './types';
import { buildToolbarButtons, createToolbarElement, positionToolbar } from './ToolbarDom';
import { ResizeManager } from './ResizeManager';
import { insertRow, insertColumn, deleteRow, deleteColumn, deleteTable } from './TableOperations';
export class TableToolbarManager {
    constructor(editor, container, options) {
        this.toolbarEl = null;
        this.cachedState = null;
        this.isToolbarAction = false;
        this.pendingFrame = 0;
        this.editor = editor;
        this.container = container;
        this.editorContainer = container.querySelector('.a-quill-editor__container') || container;
        this.onContentChange = options.onContentChange;
        this.resizeManager = new ResizeManager(editor, this.editorContainer, {
            onContentChange: this.onContentChange,
            hideToolbar: () => { if (this.toolbarEl)
                this.toolbarEl.style.display = 'none'; },
            showToolbar: () => { if (this.toolbarEl)
                this.toolbarEl.style.display = ''; },
            hideSelectionLines: () => this.hideSelectionLines(),
            refreshSelectionLines: () => this.refreshSelectionLines(),
            positionToolbar: (table) => this.updateToolbarPosition(table),
        });
        this.boundOnSelectionChange = this.onSelectionChange.bind(this);
        this.boundOnScroll = this.updatePositions.bind(this);
        this.boundOnMouseDown = this.onMouseDown.bind(this);
        this.editor.on('selection-change', this.boundOnSelectionChange);
        this.editor.root.addEventListener('scroll', this.boundOnScroll);
        this.container.addEventListener('mousedown', this.boundOnMouseDown, true);
    }
    refresh() {
        this.onSelectionChange();
    }
    destroy() {
        this.editor.off('selection-change', this.boundOnSelectionChange);
        this.editor.root.removeEventListener('scroll', this.boundOnScroll);
        this.container.removeEventListener('mousedown', this.boundOnMouseDown, true);
        cancelAnimationFrame(this.pendingFrame);
        this.removeToolbar();
        this.resizeManager.destroy();
    }
    // --- Event handlers ---
    onMouseDown(e) {
        const target = e.target;
        if (target.closest(`.${CSS_TABLE_TOOLBAR}`) || target.closest(`.${CSS_COLUMN_RESIZE_HANDLE}`) || target.closest(`.${CSS_ROW_RESIZE_HANDLE}`)) {
            e.preventDefault();
        }
    }
    onSelectionChange() {
        if (this.isToolbarAction)
            return;
        const btModule = this.editor.getModule('better-table');
        if (!btModule)
            return;
        const [table, , cell] = btModule.getTable();
        if (!table || !cell) {
            this.cachedState = null;
            this.removeToolbar();
            this.resizeManager.removeColumnHandles();
            this.resizeManager.removeRowHandles();
            return;
        }
        const tableEl = table.domNode;
        const closestTable = tableEl.closest('table') || tableEl.querySelector('table');
        const isWeekOverview = closestTable ? closestTable.hasAttribute(DATA_WEEK_OVERVIEW) : false;
        const actualTable = (closestTable || tableEl);
        this.cachedState = {
            table: actualTable,
            cell: cell.domNode,
            boundary: (btModule.tableSelection && btModule.tableSelection.boundary) || {},
            tableContainer: table,
            tableSelection: btModule.tableSelection,
            columnTool: btModule.columnTool,
        };
        cancelAnimationFrame(this.pendingFrame);
        this.pendingFrame = requestAnimationFrame(() => {
            this.showToolbar(actualTable, isWeekOverview);
            if (isWeekOverview) {
                this.resizeManager.removeColumnHandles();
                this.resizeManager.removeRowHandles();
            }
            else {
                this.resizeManager.attachColumnHandles(actualTable);
                this.resizeManager.attachRowHandles(actualTable);
            }
            // Reposition after quill-better-table finishes its deferred width updates
            setTimeout(() => this.updatePositions(), QUILL_BETTER_TABLE_REFLOW_DELAY);
        });
    }
    // --- Toolbar management ---
    showToolbar(table, isWeekOverview) {
        if (!this.toolbarEl) {
            this.toolbarEl = createToolbarElement(this.buildButtons(isWeekOverview));
            this.editorContainer.appendChild(this.toolbarEl);
        }
        else {
            this.toolbarEl.innerHTML = '';
            this.buildButtons(isWeekOverview).forEach((btn) => this.toolbarEl.appendChild(btn));
        }
        this.updateToolbarPosition(table);
    }
    buildButtons(isWeekOverview) {
        return buildToolbarButtons({
            insertRowAbove: () => this.executeOperation((s) => insertRow(this.editor, s, false)),
            insertRowBelow: () => this.executeOperation((s) => insertRow(this.editor, s, true)),
            insertColumnLeft: () => this.executeOperation((s) => insertColumn(this.editor, s, false)),
            insertColumnRight: () => this.executeOperation((s) => insertColumn(this.editor, s, true)),
            deleteRow: () => this.executeOperation((s) => deleteRow(this.editor, s)),
            deleteColumn: () => this.executeOperation((s) => deleteColumn(this.editor, s)),
            deleteTable: () => this.executeDeleteTable(),
        }, isWeekOverview);
    }
    updateToolbarPosition(table) {
        if (!this.toolbarEl)
            return;
        positionToolbar(this.toolbarEl, table, this.editorContainer);
    }
    removeToolbar() {
        if (this.toolbarEl) {
            this.toolbarEl.remove();
            this.toolbarEl = null;
        }
    }
    // --- Table operations ---
    executeOperation(operation) {
        if (!this.cachedState)
            return;
        // Refresh tableSelection/columnTool — they may not be initialized when first cached
        const btModule = this.editor.getModule('better-table');
        if (btModule) {
            if (btModule.tableSelection)
                this.cachedState.tableSelection = btModule.tableSelection;
            if (btModule.columnTool)
                this.cachedState.columnTool = btModule.columnTool;
        }
        this.isToolbarAction = true;
        try {
            operation(this.cachedState);
            this.onContentChange();
        }
        finally {
            this.isToolbarAction = false;
            setTimeout(() => this.onSelectionChange(), QUILL_BETTER_TABLE_REFLOW_DELAY);
        }
    }
    executeDeleteTable() {
        if (!this.cachedState)
            return;
        this.isToolbarAction = true;
        try {
            deleteTable(this.editor, this.cachedState);
            this.cachedState = null;
            this.removeToolbar();
            this.onContentChange();
        }
        finally {
            this.isToolbarAction = false;
        }
    }
    // --- Positioning ---
    updatePositions() {
        if (!this.cachedState)
            return;
        const table = this.cachedState.table;
        this.updateToolbarPosition(table);
        this.resizeManager.repositionAll(table);
    }
    // --- Selection line helpers ---
    hideSelectionLines() {
        this.container.querySelectorAll(`.${CSS_SELECTION_LINE}`).forEach((el) => {
            el.style.display = 'none';
        });
    }
    refreshSelectionLines() {
        if (!this.cachedState)
            return;
        const { cell, tableSelection } = this.cachedState;
        if (!tableSelection || !cell)
            return;
        const cellRect = cell.getBoundingClientRect();
        tableSelection.setSelection(cellRect, cellRect);
    }
}
